import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled"];
let _ = t => t,
  _t,
  _t2;
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLangLocale from 'I18n/utils/getLangLocale';
import PortalIdParser from 'PortalIdParser';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { InlineAutocompleteMenu } from 'content-assistance-lib/autocomplete/InlineAutocompleteMenu';
import { useFetchContentAssistanceContent } from 'content-assistance-lib/hooks/useFetchContentAssistanceContent';
import { registerQuery, useLazyQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import { getFullUrl } from 'hubspot-url-utils';
import Raven from 'raven-js';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import styled from 'styled-components';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import { SLASH_COMMANDS } from '../../plugins/ai-highlight/commands';
import { insertContent } from '../../utils/documentUtils';
import { AIAssistantOnboardingPopover } from './AIAssistantOnboardingPopover';
import { commands } from './ContentAssistantTypes';
import { useSummaryCommand } from './useSummaryCommand';
import { ReplyRecUnsuccessfulReason } from './ReplyRecUnsuccessfulReason';
import styledWithTheme from 'foundations-theming/utils';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Text } from 'foundations-components/components/Text';
import { BreezeSingleStarIcon } from 'foundations-assets/icons/components/BreezeSingleStarIcon';
import { MultiLevelDropdown } from 'foundations-components/transitional/components/Dropdown';
import { Link } from 'foundations-components/transitional/components/Link';
import { useEditorUIContext } from '../../contexts/EditorUIContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const fetchReplyRecommendationsSettings = () => {
  return http.get(`cv/replies/v1/settings`);
};
const FETCH_REPLY_RECOMMENDATION_SETTINGS = registerQuery({
  fieldName: 'fetchReplyRecommendationsSettings',
  fetcher: fetchReplyRecommendationsSettings
});
const StyledLoadingSpinner = styledWithTheme(LoadingSpinner)(_t || (_t = _`
  height: 25px;
  width: 25px;
`));
const StyledIcon = styledWithTheme(TextToolbarIcon)(_t2 || (_t2 = _`
  color: ${0};
`), ({
  disabled,
  theme
}) => disabled ? theme.color['text-core-disabled'] : theme.color['icon-tertiary-default']);
const StyledAIContainer = styled.section.withConfig({
  displayName: "AIAssistant__StyledAIContainer",
  componentId: "sc-1yc1j5w-0"
})(["display:flex;align-items:center;justify-content:center;"]);
const AIAssistant = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [aiInfoIconOpen, setAiInfoIconOpen] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    handleFirstClick
  } = useEditorUIContext();
  const {
    AI_ASSISTANT = {}
  } = useContext(EditorConfigContext);
  const {
    commandType = 'inbox',
    globalSettingEnabled = false,
    globalSummarizationEnabled = false,
    summarizationDisabledMessage = undefined,
    trackOpenPopover = undefined,
    onSelectContentAssistantOption = undefined,
    shouldShowAIOnboardingPopover = false,
    setHasSeenAIOnboardingPopover = () => {},
    replyRecommendationsConfig = {
      isReplyRecOpen: false,
      setIsReplyRecOpen: () => {},
      hasReplyRec: false,
      trackReplyRecOpen: () => {},
      unsuccessfulReason: ''
    }
  } = AI_ASSISTANT;
  const [getReplyRecSettings, {
    data: replyRecommendationsSettings
  }] = useLazyQuery(FETCH_REPLY_RECOMMENDATION_SETTINGS, {
    onError: error => {
      Raven.captureException(error);
    }
  });
  const replyRecsSettings = replyRecommendationsSettings === null || replyRecommendationsSettings === void 0 ? void 0 : replyRecommendationsSettings.fetchReplyRecommendationsSettings;
  const {
    isLoading: isGeneratingSummary,
    generateSummary
  } = useSummaryCommand();
  const {
    request
  } = useFetchContentAssistanceContent({
    language: getLangLocale(),
    onError: error => {
      console.warn('Failed to generate AI content');
      Raven.captureException(error);
    },
    onSuccess: response => {
      const tr = editorState.tr.insertText(response[0].text);
      const nextState = editorState.apply(tr);
      setEditorState(nextState);
    }
  });
  const settingsUrl = `${getFullUrl('app')}/ai-settings/${PortalIdParser.get()}`;
  const replyRecSettingsUrl = `${getFullUrl('app')}/helpdesk-settings/${PortalIdParser.get()}/inboxes/reply-recommendations`;
  const {
    isReplyRecOpen,
    setIsReplyRecOpen,
    hasReplyRec,
    trackReplyRecOpen
  } = replyRecommendationsConfig || {};
  const showReplyRecOptions = !!AI_ASSISTANT.replyRecommendationsConfig;
  useEffect(() => {
    if (showReplyRecOptions) getReplyRecSettings();
  }, [showReplyRecOptions, getReplyRecSettings]);
  const {
    replyRecommendationsEnabled,
    customerAnalysisEnabled
  } = replyRecsSettings || {};
  const getReplyRecommendationOption = () => {
    if (!showReplyRecOptions) return false;
    if (isReplyRecOpen && hasReplyRec) {
      return {
        label: /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'hubspot-prosemirror.replyRecommendations.hide'
          })
        }),
        onClick: () => {
          setIsReplyRecOpen(false);
          trackReplyRecOpen(false);
          handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('AIAssistant: hide reply recommendations');
        }
      };
    }
    if (!isReplyRecOpen && hasReplyRec) {
      return {
        label: /*#__PURE__*/_jsx(NotificationBadge, {
          badgeDescription: I18n.text('hubspot-prosemirror.replyRecommendations.generationMessages.generic'),
          size: "small",
          placement: "inline",
          showBadge: showReplyRecOptions && !isReplyRecOpen && hasReplyRec,
          children: /*#__PURE__*/_jsx(Text, {
            as: "span",
            marginInlineEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'hubspot-prosemirror.replyRecommendations.show'
            })
          })
        }),
        onClick: () => {
          setIsReplyRecOpen(true);
          trackReplyRecOpen(true);
          handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('AIAssistant: show reply recommendations');
        }
      };
    }
    return {
      label: /*#__PURE__*/_jsx(Tooltip, {
        placement: "right",
        title: customerAnalysisEnabled && replyRecommendationsEnabled ? /*#__PURE__*/_jsx(ReplyRecUnsuccessfulReason, {
          unsuccessfulReason: replyRecommendationsConfig.unsuccessfulReason
        }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: 'hubspot-prosemirror.replyRecommendations.generationMessages.inactive_jsx',
          elements: {
            Link
          },
          options: {
            href: replyRecSettingsUrl,
            external: true,
            onClick: e => {
              // Necessary to open the link while menu option/button is disabled
              e.stopPropagation();
            }
          }
        }),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'hubspot-prosemirror.replyRecommendations.show'
          })
        })
      }),
      disabled: true
    };
  };
  const toolbarAssistantOptions = [getReplyRecommendationOption(), {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.summarize'),
    onClick: () => {
      handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('AIAssistant: summarize conversation');
      generateSummary();
    },
    disabled: !globalSummarizationEnabled,
    tooltip: !globalSummarizationEnabled ? summarizationDisabledMessage || /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'hubspot-prosemirror.tooltips.customerAnalysisSettingOff_jsx',
      elements: {
        Link
      },
      options: {
        href: settingsUrl,
        external: true
      }
    }) : null
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.write'),
    onClick: () => {
      handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('AIAssistant: write for me');
      if (onSelectContentAssistantOption) {
        onSelectContentAssistantOption({
          command: 'footer_write_for_me',
          selected: ''
        });
      }
      setPopoverActive(prev => !prev);
    }
  }];
  const isFetchingCaContent = request === 'STARTED';
  const showBadge = showReplyRecOptions && hasReplyRec && !isReplyRecOpen;
  const AIAssistantButton = useCallback(_ref => {
    let {
        disabled
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(NotificationBadge, {
      badgeDescription: I18n.text('hubspot-prosemirror.replyRecommendations.replyRecommendationAvailable'),
      size: "small",
      placement: "inset",
      showBadge: showBadge,
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(RTEToolbarButton, Object.assign({}, rest, {
          disabled: disabled,
          "data-test-id": "ai-assistant",
          selected: isDropdownOpen,
          children: isGeneratingSummary || isFetchingCaContent ? /*#__PURE__*/_jsx(StyledLoadingSpinner, {
            size: "extra-small",
            use: 'primary-white',
            margin: "0"
          }) : /*#__PURE__*/_jsxs(StyledAIContainer, {
            children: [shouldShowAIOnboardingPopover && /*#__PURE__*/_jsx(AIAssistantOnboardingPopover, {
              aiInfoIconOpen,
              setAiInfoIconOpen,
              setHasSeenAIOnboardingPopover,
              settingsUrl
            }), /*#__PURE__*/_jsx(Tooltip, {
              open: isDropdownOpen ? false : undefined,
              title: globalSettingEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: 'hubspot-prosemirror.tooltips.aiAssistant'
              }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "hubspot-prosemirror.tooltips.contentAssistantSettingOff_jsx",
                elements: {
                  Link
                },
                options: {
                  href: settingsUrl,
                  external: true,
                  onClick: e => {
                    // Necessary to open the link while menu option/button is disabled
                    e.stopPropagation();
                  }
                }
              }),
              children: /*#__PURE__*/_jsx(StyledIcon, {
                disabled: !globalSettingEnabled,
                icon: BreezeSingleStarIcon
              })
            })]
          })
        }))
      })
    });
  }, [isDropdownOpen, isGeneratingSummary, isFetchingCaContent, shouldShowAIOnboardingPopover, aiInfoIconOpen, setAiInfoIconOpen, setHasSeenAIOnboardingPopover, settingsUrl, globalSettingEnabled, showBadge]);
  const showPopover = popoverActive && !isDropdownOpen;
  const onPromptComplete = htmlBody => {
    const nextEditorState = insertContent({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(nextEditorState);
    setPopoverActive(false);
  };
  return /*#__PURE__*/_jsx(Popover, {
    open: showPopover,
    use: 'unstyled',
    placement: "bottom",
    content: /*#__PURE__*/_jsx(InlineAutocompleteMenu, {
      currentCommand: SLASH_COMMANDS[commands[commandType].write],
      onPromptComplete: onPromptComplete,
      onClose: () => {
        setPopoverActive(false);
      },
      trackInteraction: action => {
        if (onSelectContentAssistantOption) {
          onSelectContentAssistantOption({
            selected: action,
            command: 'footer_write_for_me'
          });
        }
      },
      editorContext: '' // set to empty since text in editor is currently not relevant to AI Assistant Toolbar usage
      ,
      usageDescription: 'Inbox toolbar AI Assistant'
    }),
    children: /*#__PURE__*/_jsx(MultiLevelDropdown, {
      Button: AIAssistantButton,
      buttonUse: 'transparent',
      disabled: !globalSettingEnabled,
      open: isDropdownOpen,
      onOpenChange: e => {
        setIsDropdownOpen(e.target.value);
      },
      onOpenComplete: () => {
        if (trackOpenPopover) {
          trackOpenPopover();
        }
      },
      options: toolbarAssistantOptions
    })
  });
};
export default AIAssistant;